import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "../ui/button";
import LearnMoreSection from "../LearnMore";
import { CardTitle, CardDescription, CardHeader, CardContent, Card } from "../ui/card";
import {
  PlugZap,
  DatabaseZap,
  Bot,
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  Rocket
} from 'lucide-react';

export default function HeroHome() {
  return (
    <>
      <div className="flex flex-col min-h-screen bg-background text-foreground relative overflow-hidden">

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-12 md:py-20 lg:py-28">
          {/* Background elements */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-background/80 z-0"></div>
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiMwMDAwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMiIgZD0iTTM2IDM0aDR2MWgtNHYtMXptMC0yaDF2NGgtMXYtNHptMi0yaDF2MWgtMXYtMXptLTIgMmgtMXYtMWgxdjF6TTM0IDI5aDR2MWgtNHYtMXptMC0yaDF2NGgtMXYtNHptMi0yaDF2MWgtMXYtMXptLTIgMmgtMXYtMWgxdjF6Ii8+PC9nPjwvc3ZnPg==')] opacity-[0.015] dark:opacity-[0.03]"></div>
          
          <div className="container relative z-10 px-4 md:px-6 mx-auto max-w-5xl">
            <div className="flex flex-col items-center space-y-8 text-center">
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Sparkles className="w-4 h-4 mr-2" />
                <span>AI-Powered Data Intelligence</span>
              </div>
              
              {/* Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-accent leading-tight">
                Unlock Your Data.<br />Unleash Your Potential.
              </h1>
              
              {/* Description */}
              <p className="mx-auto max-w-[700px] text-foreground/70 text-lg md:text-xl lg:text-2xl leading-relaxed">
                TenXer provides a secure, conversational interface to your existing APIs and databases.
                Build intelligent agents, automate workflows, and accelerate development <span className="font-bold text-primary">10x faster</span>.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mt-4">
                <Link to="/login" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto text-lg font-medium px-8 py-6 h-auto">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link target="_blank" to="https://docs.google.com/forms/d/e/1FAIpQLSc-PDrAc0YvPgw2krZFCIye46g7-Os8arB1yNpl2X-PQgOMEA/viewform?usp=header" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg font-medium px-8 py-6 h-auto border-primary/20 text-primary hover:bg-primary/10">
                    Schedule Demo
                  </Button>
                </Link>
              </div>
              
              {/* Trust indicators */}
              <div className="flex items-center justify-center gap-2 pt-8 text-foreground/40 text-sm">
                <Shield className="h-4 w-4" />
                <span>Enterprise-grade security</span>
                <span className="mx-2">•</span>
                <Zap className="h-4 w-4" />
                <span>Lightning-fast setup</span>
                <span className="mx-2">•</span>
                <Rocket className="h-4 w-4" />
                <span>Crafted with care by experts</span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="relative py-20 md:py-28">
          {/* Subtle separator */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
          
          <div className="container px-4 md:px-6 mx-auto max-w-6xl">
            <div className="flex flex-col items-center justify-center space-y-4 text-center mb-16">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium">
                <Zap className="w-4 h-4 mr-2" />
                <span>Key Features</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">How TenXer Works</h2>
              <p className="max-w-[900px] text-foreground/70 text-lg md:text-xl leading-relaxed">
                Securely connect your data sources and interact with them using natural language,
                powered by lightweight, intelligent agents that understand your business context.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 1 */}
              <div className="group relative">
                <div className="absolute -inset-px bg-gradient-to-r from-primary to-primary/50 rounded-xl blur opacity-25 group-hover:opacity-100 transition duration-300"></div>
                <Card className="relative h-full border-0 bg-card/80 backdrop-blur-sm">
                  <CardHeader className="pb-4">
                    <div className="mb-3 inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary">
                      <PlugZap className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl">Connect APIs Instantly</CardTitle>
                    <CardDescription className="text-foreground/70 text-base">
                      Register your existing REST APIs. TenXer understands the structure and lets you query them through chat, no coding required.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-foreground/70">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-primary text-xs">✓</span>
                        </div>
                        <span>Automatic schema detection</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-primary text-xs">✓</span>
                        </div>
                        <span>OAuth & API key support</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-primary text-xs">✓</span>
                        </div>
                        <span>Custom headers & parameters</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              {/* Card 2 */}
              <div className="group relative">
                <div className="absolute -inset-px bg-gradient-to-r from-secondary to-secondary/50 rounded-xl blur opacity-25 group-hover:opacity-100 transition duration-300"></div>
                <Card className="relative h-full border-0 bg-card/80 backdrop-blur-sm">
                  <CardHeader className="pb-4">
                    <div className="mb-3 inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/10 text-secondary">
                      <DatabaseZap className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl">Access Databases Securely</CardTitle>
                    <CardDescription className="text-foreground/70 text-base">
                      Deploy our lightweight agent within your VPC to securely connect to SQL databases. Chat with your relational data.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-foreground/70">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-secondary/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-secondary text-xs">✓</span>
                        </div>
                        <span>PostgreSQL, MySQL, SQL Server</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-secondary/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-secondary text-xs">✓</span>
                        </div>
                        <span>Zero data leaves your environment</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-secondary/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-secondary text-xs">✓</span>
                        </div>
                        <span>Role-based access controls</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              {/* Card 3 */}
              <div className="group relative">
                <div className="absolute -inset-px bg-gradient-to-r from-accent to-accent/50 rounded-xl blur opacity-25 group-hover:opacity-100 transition duration-300"></div>
                <Card className="relative h-full border-0 bg-card/80 backdrop-blur-sm">
                  <CardHeader className="pb-4">
                    <div className="mb-3 inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent/10 text-accent">
                      <Bot className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl">Intelligent Agents</CardTitle>
                    <CardDescription className="text-foreground/70 text-base">
                      Agents handle data fetching, understand context, and can perform multi-step operations based on your requests.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-foreground/70">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-accent text-xs">✓</span>
                        </div>
                        <span>Contextual understanding</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-accent text-xs">✓</span>
                        </div>
                        <span>Multi-step reasoning</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center mr-2 mt-0.5">
                          <span className="text-accent text-xs">✓</span>
                        </div>
                        <span>Automated workflow execution</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Learn More Section */}
        <LearnMoreSection />
        
      </main>
      
      {/* Footer */}
      <footer className="relative border-t border-border/40">
        <div className="absolute inset-0 bg-gradient-to-b from-background to-background/80 z-0"></div>
        <div className="container relative z-10 px-4 py-8 mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-2">
                <Zap className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm text-foreground/60">© 2024 TenXer. All rights reserved.</p>
            </div>
            <nav className="flex gap-6">
              <Link className="text-sm text-foreground/60 hover:text-primary transition-colors" to="/terms">
                Terms of Service
              </Link>
              <Link className="text-sm text-foreground/60 hover:text-primary transition-colors" to="/privacy">
                Privacy Policy
              </Link>
              <Link className="text-sm text-foreground/60 hover:text-primary transition-colors" to="/security">
                Security
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}